/**
 * 分拣过程动画 —— 模块出口
 *
 * 数据流：fetchProcessTimeline → FrameProvider（Reconstructor 懒重建 ProcessFrame[]）→
 * mountProcessPlayer → FramePlayer（按 provider 取帧）→ SceneRenderer（消费 Frame）。
 * 后端过程接口就绪前，fetchProcessTimeline 自动回退到 mock ProcessFrame[]（仍走重建路径）。
 */

export { generateFrames } from './mock';
export { generateProcessFrames } from './mock-process';
export { FramePlayer, isValidFrames } from './player';
export type { PlayerSnapshot, PlayerListener } from './player';
export { SceneRenderer } from './renderer';
export { mountProcessPlayer } from './controller';
export type { ProcessPlayerHandle } from './controller';
export { Reconstructor, ArrayFrameProvider, isValidProcessFrames } from './reconstructor';
export * from './types';

import { generateProcessFrames } from './mock-process';
import { Reconstructor, isValidProcessFrames } from './reconstructor';
import type { FrameProvider, ProcessFrame, ProcessTimelineInput } from './types';

/**
 * 后端过程时间线接口（provisional 路径，对齐 sortingPlan 域现有约定 `/api/v1/wfp/sortingPlan/...`；
 * contract.md §8.6 另记 `/api/v1/sorting-plan/process-timeline`，待后端落地裁定）。经 _worker.js
 * 反代 /api/* → 真实 Java 服务。
 */
const PROCESS_TIMELINE_URL = '/api/v1/wfp/sortingPlan/process-timeline';

/**
 * 获取过程时间线，返回 FrameProvider（Reconstructor 懒重建）。
 *
 * 优先请求真实后端（POST，入参 marks + config）；失败/未就绪时回退到 mock ProcessFrame[]
 * （generateProcessFrames），两者都走同一重建路径。空/非法数据由调用方按 provider.length 判定。
 */
export async function fetchProcessTimeline(input: ProcessTimelineInput): Promise<FrameProvider> {
  try {
    const resp = await fetch(PROCESS_TIMELINE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ marks: input.marks, config: input.config }),
    });
    if (resp.ok) {
      const data = (await resp.json()) as ProcessFrame[];
      if (isValidProcessFrames(data)) return new Reconstructor(data);
    }
  } catch {
    // 后端未就绪 / 网络错误 → 回退 mock
  }
  return new Reconstructor(generateProcessFrames(input.marks, input.config));
}
